/**
 * THE SINGLE UNIFIED KEYBOARD HANDLER FOR ALL MODALS
 *
 * This is the ONLY keyboard handler for modals. Period.
 * It provides a simple, consistent interface for handling keyboard events.
 *
 * PRINCIPLES:
 * 1. One handler to rule them all - no duplicate systems
 * 2. Simple event bubbling - let DOM handle propagation naturally
 * 3. Consistent behavior across all modals
 * 4. No complex state management or stores
 *
 * DO NOT CREATE OTHER KEYBOARD HANDLERS!
 */

import { ref, onMounted, onUnmounted, watch, Ref } from 'vue';
import { createLogger } from '@/utils/logger';

const logger = createLogger('ModalKeyboard');

export interface ModalKeyboardOptions {
  /** Reference to the modal's root element for scoping */
  modalRef: Ref<HTMLElement | undefined>;

  /** Optional visibility state to prevent capturing keys when modal is hidden */
  isVisible?: Ref<boolean>;

  /**
   * Selection mode for the modal.
   * - 'single': Single item selection mode (Enter auto-selects focused item)
   * - 'multi': Multi-item selection mode (Enter saves current selections)
   * - undefined: Default behavior (Enter saves)
   */
  mode?: 'single' | 'multi';

  /**
   * Function to get the search results count.
   * Used in single-select mode to determine if Enter should auto-select.
   */
  getSearchResultsCount?: () => number;

  /**
   * Function to focus the first item in the list.
   * Used in single-select mode when Enter is pressed on search with multiple results.
   */
  focusFirstListItem?: () => void;

  /** Simple handlers for keyboard shortcuts */
  handlers: {
    // Navigation
    onArrowUp?: () => void;
    onArrowDown?: () => void;
    onArrowLeft?: () => void;
    onArrowRight?: () => void;

    // Actions
    onEnter?: () => void;
    onEscape?: () => void;
    onSpace?: () => void;

    // Save/Commit (Ctrl/Cmd+Enter)
    onCtrlEnter?: () => void;
    onSaveCommit?: () => void; // Alias for clarity

    // Shortcuts
    onSlash?: () => void; // '/' - Focus search
    onQuestionMark?: () => void; // '?' - Focus search (alias)
    onA?: () => void; // 'a' - Toggle archived
    onN?: () => void; // 'n' - Select none
    onS?: () => void; // 's' - Toggle subtasks
    onC?: () => void; // 'c' - Clear all
    onDelete?: () => void; // Delete key

    // Select all (Ctrl/Cmd+A)
    onSelectAll?: () => void;
  };

  /** Enable debug logging */
  debug?: boolean;
}

/**
 * The ONE AND ONLY modal keyboard handler
 *
 * Usage:
 * ```typescript
 * const keyboard = useModalKeyboard({
 *   modalRef: dialogWrapperRef,
 *   handlers: {
 *     onEnter: () => save(),
 *     onEscape: () => cancel(),
 *     onArrowDown: () => navigateNext(),
 *     onArrowUp: () => navigatePrev(),
 *   }
 * });
 * ```
 */
export function useModalKeyboard(options: ModalKeyboardOptions) {
  const { modalRef, handlers, isVisible } = options;
  const isActive = ref(false);
  // Track whether we're using the document fallback
  const usingDocumentFallback = ref(false);

  /**
   * Main keyboard event handler
   * IMPORTANT: This should be the ONLY keyboard handler in the modal system
   */
  const handleKeyDown = (event: KeyboardEvent) => {
    // Debug logging when debug mode is enabled
    if (
      options.debug &&
      (event.key === 'Escape' || (event.metaKey && event.key === 'Enter'))
    ) {
      logger.debug('Key event:', {
        key: event.key,
        metaKey: event.metaKey,
        ctrlKey: event.ctrlKey,
        isActive: isActive.value,
        isVisible: isVisible?.value,
        usingDocumentFallback: usingDocumentFallback.value,
        modalRefExists: !!modalRef.value,
        targetContained: modalRef.value?.contains(event.target as Node),
        target: event.target,
      });
    }

    // Check both isActive AND isVisible (if provided)
    if (!isActive.value || (isVisible && !isVisible.value)) {
      return;
    }

    // Only handle events within our modal
    // When attached to modal element: events are already scoped
    // When using document fallback: check containment manually
    // If modalRef is undefined (modal not rendered), skip handling
    if (usingDocumentFallback.value) {
      // Using document fallback - must check containment
      if (!modalRef.value || !modalRef.value.contains(event.target as Node)) {
        return;
      }
    }
    // When attached to modal element, events are already scoped by capture mode

    const target = event.target as HTMLElement;
    const tagName = target.tagName;
    const isInInput =
      tagName === 'INPUT' || tagName === 'TEXTAREA' || tagName === 'SELECT';
    const isContentEditable = target.contentEditable === 'true';

    // Check if we're in an interactive control that should handle its own arrow keys
    // Note: We do NOT check for menus/dropdowns here because Escape should always close the modal
    const isInInteractiveControl =
      isInInput ||
      isContentEditable ||
      // Check for expanded dropdowns/comboboxes (for arrow key navigation only)
      target.closest('[role="listbox"]') ||
      target.closest('[role="combobox"]') ||
      target.closest('[aria-expanded="true"]');

    // Handle Ctrl/Cmd+A for select all
    if ((event.ctrlKey || event.metaKey) && event.key === 'a') {
      if (handlers.onSelectAll) {
        event.preventDefault();
        handlers.onSelectAll();
        return;
      }
    }

    // Map keys to handlers
    let handler: (() => void) | undefined;

    switch (event.key) {
      // Navigation keys - only handle if NOT in an interactive control
      case 'ArrowUp':
        if (!isInInteractiveControl) {
          handler = handlers.onArrowUp;
        }
        break;
      case 'ArrowDown':
        if (!isInInteractiveControl) {
          handler = handlers.onArrowDown;
        }
        break;
      case 'ArrowLeft':
        if (!isInInteractiveControl) {
          handler = handlers.onArrowLeft;
        }
        break;
      case 'ArrowRight':
        if (!isInInteractiveControl) {
          handler = handlers.onArrowRight;
        }
        break;

      // Action keys
      case 'Enter': {
        // Check for Ctrl/Cmd+Enter for save/commit (NOT Shift+Enter - that's for newlines in textareas)
        if (event.ctrlKey || event.metaKey) {
          if (options.debug) {
            logger.debug('Ctrl/Cmd+Enter detected', {
              isInInput,
              isContentEditable,
              hasHandler: !!(handlers.onCtrlEnter || handlers.onSaveCommit),
            });
          }
          // Always prevent default for Cmd/Ctrl+Enter to avoid browser interference
          event.preventDefault();
          event.stopPropagation();

          // If we're in an input field, schedule save with small delay for Vue reactivity
          // This ensures v-model has updated before validation runs
          if (isInInput || isContentEditable) {
            if (handlers.onCtrlEnter || handlers.onSaveCommit) {
              if (options.debug) {
                logger.debug('Scheduling save via setTimeout (in input)');
              }
              // Small delay to allow Vue's v-model to update
              setTimeout(() => {
                if (options.debug) {
                  logger.debug('Executing delayed save handler');
                }
                (handlers.onCtrlEnter || handlers.onSaveCommit)?.();
              }, 0);
            }
            return;
          }
          // Not in an input - handle immediately
          handler = handlers.onCtrlEnter || handlers.onSaveCommit;
          break;
        }

        // ENTER NOW MIRRORS SPACE BAR EXACTLY
        // Only handle Enter on list items to select/toggle them
        const enterListItem =
          target.closest('[role="option"]') ||
          target.closest('.item-selector__content-item') ||
          target.closest('[data-navigable="true"]');

        if (enterListItem) {
          // Check if this is a hierarchical/folder item (should only navigate, not save)
          const isHierarchical =
            enterListItem.classList.contains(
              'item-selector__content-item--hierarchical'
            ) || enterListItem.hasAttribute('data-hierarchical');

          // Check if this item is disabled (not selectable)
          // Disabled items can still navigate if they have children, but should never trigger save
          const isDisabled =
            enterListItem.classList.contains(
              'item-browser-display__row--disabled'
            ) || enterListItem.hasAttribute('data-disabled');

          // Trigger synthetic click on the list item (same as Space)
          const clickEvent = new MouseEvent('click', {
            bubbles: true,
            cancelable: true,
            view: window,
            detail: 1,
            ...{ fromKeyboard: true },
          });
          enterListItem.dispatchEvent(clickEvent);
          // Prevent default to avoid any form submission
          event.preventDefault();
          event.stopPropagation();

          // SINGLE-SELECTOR BEHAVIOR: In single mode, Enter also saves and closes
          // Multi mode requires explicit Ctrl/Cmd+Enter to save
          // BUT: Don't save if this is a hierarchical item (folder) - only navigate
          // AND: Don't save if the item is disabled - it can't be selected
          if (
            options.mode === 'single' &&
            handlers.onEnter &&
            !isHierarchical &&
            !isDisabled
          ) {
            // Give the click event a chance to complete
            setTimeout(() => {
              handlers.onEnter?.();
            }, 0);
          }

          return; // Early return since we handled it
        }

        // If not on a list item, Enter does nothing (no save, no navigation)
        // User must use Ctrl/Cmd+Enter to save
        break;
      }
      case 'Escape':
        // ALWAYS handle Escape to close the modal, regardless of where focus is
        // Prevent immediately to stop any other handlers (dropdowns, inputs, etc.)
        if (handlers.onEscape) {
          event.preventDefault();
          event.stopPropagation();
          if (options.debug) {
            logger.debug('Escape handler called');
          }
          handlers.onEscape();
        }
        return; // Already handled, skip the generic handler execution
      case ' ': {
        // Only handle space on list items to prevent page scroll
        const spaceListItem =
          target.closest('[role="option"]') ||
          target.closest('.item-selector__content-item') ||
          target.closest('[data-navigable="true"]');

        if (spaceListItem) {
          // Check if this is a hierarchical/folder item (should only navigate, not save)
          const isHierarchical =
            spaceListItem.classList.contains(
              'item-selector__content-item--hierarchical'
            ) || spaceListItem.hasAttribute('data-hierarchical');

          // Check if this item is disabled (not selectable)
          // Disabled items can still navigate if they have children, but should never trigger save
          const isDisabled =
            spaceListItem.classList.contains(
              'item-browser-display__row--disabled'
            ) || spaceListItem.hasAttribute('data-disabled');

          // Trigger synthetic click on the list item
          const clickEvent = new MouseEvent('click', {
            bubbles: true,
            cancelable: true,
            view: window,
            detail: 1, // Set detail to 1 to indicate a real click
            ...{ fromKeyboard: true },
          });
          spaceListItem.dispatchEvent(clickEvent);
          // Prevent default to stop page scroll
          event.preventDefault();
          event.stopPropagation();

          // SINGLE-SELECTOR BEHAVIOR: In single mode, Space also saves and closes
          // Multi mode requires explicit Ctrl/Cmd+Enter to save
          // BUT: Don't save if this is a hierarchical item (folder) - only navigate
          // AND: Don't save if the item is disabled - it can't be selected
          if (
            options.mode === 'single' &&
            handlers.onEnter &&
            !isHierarchical &&
            !isDisabled
          ) {
            // Give the click event a chance to complete
            setTimeout(() => {
              handlers.onEnter?.();
            }, 0);
          }

          return; // Early return since we handled it
        }
        break;
      }
      case 'Delete':
        handler = handlers.onDelete;
        break;

      // Shortcut keys - only handle when NOT in input
      case '/':
        if (!isInInput && !isContentEditable) {
          handler = handlers.onSlash;
        }
        break;
      case '?':
        if (!isInInput && !isContentEditable) {
          handler = handlers.onQuestionMark;
        }
        break;
      case 'a':
      case 'A':
        if (!isInInput && !isContentEditable) {
          handler = handlers.onA;
        }
        break;
      case 'n':
      case 'N':
        if (!isInInput && !isContentEditable) {
          handler = handlers.onN;
        }
        break;
      case 's':
      case 'S':
        if (!isInInput && !isContentEditable) {
          handler = handlers.onS;
        }
        break;
      case 'c':
      case 'C':
        if (!isInInput && !isContentEditable) {
          handler = handlers.onC;
        }
        break;
    }

    // Execute handler if found
    if (handler) {
      // Only prevent default for keys we're actually handling
      event.preventDefault();
      event.stopPropagation();
      handler();
    }
  };

  // Track which element we're currently attached to (for cleanup during element recreation)
  let currentAttachedElement: HTMLElement | null = null;

  /**
   * Attach handler to a specific element
   */
  const attachToElement = (element: HTMLElement) => {
    // Clean up old attachment first
    if (currentAttachedElement && currentAttachedElement !== element) {
      currentAttachedElement.removeEventListener(
        'keydown',
        handleKeyDown,
        true
      );
    }
    if (usingDocumentFallback.value) {
      document.removeEventListener('keydown', handleKeyDown, true);
      usingDocumentFallback.value = false;
    }

    element.addEventListener('keydown', handleKeyDown, true);
    currentAttachedElement = element;
    isActive.value = true;
  };

  /**
   * Attach handler to document (fallback)
   */
  const attachToDocument = () => {
    // Clean up old attachment first
    if (currentAttachedElement) {
      currentAttachedElement.removeEventListener(
        'keydown',
        handleKeyDown,
        true
      );
      currentAttachedElement = null;
    }

    document.addEventListener('keydown', handleKeyDown, true);
    usingDocumentFallback.value = true;
    isActive.value = true;
  };

  /**
   * Activate keyboard handling
   * Prefers attaching to modal element for proper scoping,
   * falls back to document if modal element not yet available.
   */
  const activate = () => {
    if (isActive.value) return;

    if (modalRef.value) {
      attachToElement(modalRef.value);
    } else {
      // Modal element not yet rendered (QDialog content pending)
      // Use document fallback temporarily
      attachToDocument();
    }
  };

  /**
   * Deactivate keyboard handling
   */
  const deactivate = () => {
    if (!isActive.value) return;

    if (currentAttachedElement) {
      currentAttachedElement.removeEventListener(
        'keydown',
        handleKeyDown,
        true
      );
      currentAttachedElement = null;
    }
    if (usingDocumentFallback.value) {
      document.removeEventListener('keydown', handleKeyDown, true);
    }
    isActive.value = false;
    usingDocumentFallback.value = false;
  };

  // Auto-activate when composable is mounted
  onMounted(() => {
    activate();
  });

  // Watch for modalRef changes to handle:
  // 1. Initial render: undefined -> element (re-attach from document to modal)
  // 2. Element recreation during re-render: old element -> new element
  // 3. Modal closing: element -> undefined (handled by deactivate on unmount)
  watch(
    () => modalRef.value,
    (newValue, oldValue) => {
      if (!isActive.value) return;

      if (newValue && newValue !== currentAttachedElement) {
        // Modal element is available and different from current attachment
        // Re-attach to the (possibly new) modal element
        attachToElement(newValue);
      } else if (!newValue && oldValue && currentAttachedElement) {
        // Modal element became unavailable - fall back to document
        // This handles edge cases where modal is briefly hidden
        attachToDocument();
      }
    },
    { immediate: true }
  );

  // Clean up on unmount
  onUnmounted(() => {
    deactivate();
  });

  return {
    isActive,
    activate,
    deactivate,
    // Expose the handler for testing
    handleKeyDown,
  };
}

// For backward compatibility - just re-export the same function
export const useModalKeyboardHandler = useModalKeyboard;
